import React from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import provisionSucess from "./../../assets/svgIcons/provision-sucess.svg";
import provisionSucessMobile from "./../../assets/svgIcons/provisional-success-mobile.svg";
import leftConfetti from "./../../assets/svgIcons/left-confetti.svg";
import rightConfetti from "./../../assets/svgIcons/right-confetti.svg";

interface ProvisionalOfferCardProps {
  studentName?: string;
}

const ProvisionalOfferCard: React.FC<ProvisionalOfferCardProps> = ({
  studentName,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Mobile view that matches the image exactly
  if (isMobile) {
    return (
      <Box
        sx={{
          backgroundColor: "rgba(0, 179, 181, 0.08)",
          borderRadius: "6px",
          border: "1px solid rgba(0, 179, 181, 1)",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          minHeight: "280px",
          mb: 2,
        }}
      >
        {/* Header Illustration Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            zIndex: 1,
            p: 2,
          }}
        >
          {/* Envelope Icon with Document */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              my: 1,
              position: "relative",
            }}
          >
            <img
              src={provisionSucessMobile}
              alt="Provisional Letter Icon"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Main Congratulations Text */}
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#10656f",
              fontFamily: "Jost",
              mb: 0.5,
              lineHeight: "1.2",
            }}
          >
            Congratulations, {studentName || ""}!
          </Typography>

          {/* Offer Text */}
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 700,
              color: "#10656f",
              fontFamily: "Jost",
              mb: 1,
              lineHeight: "1.2",
            }}
          >
            You've received a Provisional Loan Offer from us!
          </Typography>

          {/* Disclaimer Text */}
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#10656f",
              fontFamily: "Jost",
              lineHeight: "1.4",
              maxWidth: "90%",
            }}
          >
            Based on the information you've provided, we're excited to share
            this offer designed to support your academic journey.
          </Typography>
        </Box>

        {/* Separator Line */}
        <Box
          sx={{
            width: "100%",
            height: "1px",
            backgroundColor: "#00b3b5",
          }}
        />

        {/* Call to Action Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            backgroundColor: "white",
            width: "100%",
            height: "100%",
            py: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#10656f",
              fontFamily: "Jost",
              mb: 0.5,
              lineHeight: "1.4",
            }}
          >
            To move forward, just complete your application,{" "}
          </Typography>
          {/* <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 800,
              color: "#cb9a0a",
              fontFamily: "Jost",
              lineHeight: "1.4",
            }}
          >
            and you'll receive your Sanction Letter within 48 hours.
          </Typography> */}
        </Box>
      </Box>
    );
  }

  // Desktop view - keep existing unchanged
  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2 }, // Smaller padding on small screens
        mb: 2,
        backgroundColor: "#ccf0f0",
        borderRadius: "6px",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      {/* Main Content (Envelope, Congratulations, Offer Text) */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: { xs: 1, sm: 2 }, // Smaller vertical padding on small screens
          px: { xs: 1, sm: 0 }, // Add some horizontal padding on small screens if text gets too wide
          width: { xs: "95%", sm: "100%" }, // Ensure content doesn't stretch full width on tiny screens
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 5 }}>
          {/* Left Confetti Cone */}
          <img
            src={leftConfetti}
            alt="Confetti Left"
            style={{ objectFit: "contain" }}
          />

          <Box>
            {/* Envelope Icon */}
            <img src={provisionSucess} alt="Provisional Letter Icon" />

            {/* Congratulations Text */}
            <Typography
              className="text1"
              sx={{
                mt: { xs: 0.5 },
                fontSize: { xs: "28", sm: "34px" }, // Adjust h5 size for mobile (defaults to 2.125rem)
              }}
            >
              Congratulations, {studentName || ""}!
            </Typography>
          </Box>

          {/* Right Confetti Cone */}
          <img
            src={rightConfetti}
            alt="Confetti Right"
            style={{ objectFit: "contain" }}
          />
        </Box>

        {/* Offer Text */}
        <Typography
          className="text2"
          sx={{
            my: 1.5,
            fontSize: { xs: "22px", sm: "28px" },
            lineHeight: { xs: "1.3rem", sm: "1.5rem" },
          }}
        >
          You've received a Provisional Loan Offer from us!
        </Typography>

        {/* Disclaimer Text */}
        <Typography
          className="text3"
          sx={{
            maxWidth: { xs: "95%", sm: "80%" }, // Narrower text on small screens
            lineHeight: { xs: 1.4, sm: 1.5 },
            fontSize: { xs: "11", sm: "13px" }, // Adjust body2 size for mobile (defaults to 0.875rem)
          }}
        >
          Based on the information you've provided, we're excited to share this
          offer designed to support your academic journey.
        </Typography>
        <Typography
          className="text3"
          sx={{
            maxWidth: { xs: "95%", sm: "80%" }, // Narrower text on small screens
            lineHeight: { xs: 1.4, sm: 1.5 },
            fontSize: { xs: "11", sm: "13px" }, // Adjust body2 size for mobile (defaults to 0.875rem)
          }}
        >
          To move forward, just complete your application.
          {/* <span className="text4Orange">
            and you'll receive your Sanction Letter within 48 hours.
          </span> */}
        </Typography>
      </Box>
    </Box>
  );
};

export default ProvisionalOfferCard;
